import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type { User, Project } from 'src/types';
import CheckboxSelect from 'src/UtilComponents/CheckboxSelect';

const PASSWORD_MIN_LENGTH = 8;
const PASSWORD_MAX_LENGTH = 72;

interface UserFormProps {
  initialData?: User;
  projects: Project[];
  onSubmit: (data: Partial<User> & { password?: string }) => void;
  isLoading: boolean;
  onCancel: () => void;
}

const UserForm = ({ initialData, projects, onSubmit, isLoading, onCancel }: UserFormProps) => {
  const { t } = useTranslation();
  const isEditing = !!initialData;

  const [name, setName] = useState(initialData?.name ?? '');
  const [email, setEmail] = useState(initialData?.email ?? '');
  const [password, setPassword] = useState('');
  const [isAdmin, setIsAdmin] = useState(initialData?.is_admin ?? false);
  const [preferredLanguage, setPreferredLanguage] = useState(
    initialData?.preferred_language ?? 'en'
  );
  const [selectedProjects, setSelectedProjects] = useState<string[]>(
    initialData?.project_ids ?? []
  );

  const handleAdminChange = (checked: boolean) => {
    setIsAdmin(checked);
    if (checked) {
      setSelectedProjects(projects.map((p) => p.id));
    }
  };

  const passwordTooShort = password.length > 0 && password.length < PASSWORD_MIN_LENGTH;
  const passwordTooLong = password.length > PASSWORD_MAX_LENGTH;
  const passwordValid = password.length >= PASSWORD_MIN_LENGTH && !passwordTooLong;
  const passwordRequired = !isEditing && password.length === 0;

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (passwordRequired || passwordTooShort || passwordTooLong) return;
    const data: Partial<User> & { password?: string } = {
      name,
      email,
      is_admin: isAdmin,
      preferred_language: preferredLanguage,
      project_ids: selectedProjects,
    };
    if (password) data.password = password;
    onSubmit(data);
  };

  return (
    <form className="management-form" onSubmit={handleSubmit}>
      <div className="management-form-field">
        <label htmlFor="user-name">{t('management.user.name')} *</label>
        <input
          id="user-name"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
        />
      </div>
      <div className="management-form-field">
        <label htmlFor="user-email">{t('management.user.email')} *</label>
        <input
          id="user-email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
      </div>
      <div className="management-form-field">
        <label htmlFor="user-password">
          {t('management.user.password')} {!isEditing && '*'}
        </label>
        <input
          id="user-password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder={isEditing ? t('management.user.passwordHint') : undefined}
        />
        {password.length > 0 ? (
          <span
            className={`management-form-hint ${passwordValid ? 'management-form-hint--valid' : 'management-form-hint--error'}`}
          >
            {password.length}/{PASSWORD_MIN_LENGTH} {t('management.user.characters', 'characters')}
          </span>
        ) : (
          <span className="management-form-hint">
            {isEditing
              ? t(
                  'management.user.passwordHintEdit',
                  'Leave blank to keep current password (min. 8 characters)'
                )
              : t('management.user.passwordHintCreate', 'Must be at least 8 characters')}
          </span>
        )}
      </div>
      <div className="management-form-field management-form-checkbox">
        <input
          id="user-admin"
          type="checkbox"
          checked={isAdmin}
          onChange={(e) => handleAdminChange(e.target.checked)}
        />
        <label htmlFor="user-admin">{t('management.user.isAdmin')}</label>
      </div>
      <div className="management-form-field">
        <label htmlFor="user-language">{t('management.user.language')}</label>
        <select
          id="user-language"
          value={preferredLanguage}
          onChange={(e) => setPreferredLanguage(e.target.value)}
        >
          <option value="en">{t('language.en')}</option>
          <option value="de">{t('language.de')}</option>
        </select>
      </div>
      {!isAdmin && (
        <div className="management-form-field">
          <label>{t('management.user.projects')}</label>
          <CheckboxSelect
            options={projects.map((p) => ({ id: p.id, name: p.name }))}
            selectedOptions={selectedProjects}
            onChange={setSelectedProjects}
            isLoading={false}
          />
        </div>
      )}
      <div className="entity-modal-footer" style={{ padding: 0, border: 'none' }}>
        <button
          type="button"
          className="management-btn management-btn--secondary"
          onClick={onCancel}
        >
          {t('management.cancel')}
        </button>
        <button
          type="submit"
          className="management-btn management-btn--primary"
          disabled={isLoading}
        >
          {isLoading ? t('management.saving') : t('management.save')}
        </button>
      </div>
    </form>
  );
};

export default UserForm;
